import React from 'react';
import {
    Box,
    Dialog,
    DialogContent,
    DialogTitle,
    TextField,
    Autocomplete,
    DialogActions,
    Button,
    TextFieldProps,
    Checkbox,
    DialogContentText,
    CircularProgress,
    debounce,
    useTheme
} from '@mui/material';
import * as companyService from 'services/company-service';
import { useTranslation } from 'react-i18next';
import { useFormik } from 'formik';
import * as Yup from 'yup'
// import debounce from 'lodash/debounce';

interface IProps {
    open: boolean;
    onClose: () => void;
    refresh: () => void;
    tenantId: string;
}


const CreateResponsibleModal = (props: IProps) => {
    const { open, onClose, refresh, tenantId } = props;
    const { t } = useTranslation();
    const [companies, _setCompanies] = React.useState<Company[]>([]);
    const [orgInput, setOrgInput] = React.useState<string>("");
    const [company, setCompany] = React.useState<Company[]>([]);
    const [companyError, setCompanyError] = React.useState<{ error: boolean, text: string }>({ error: false, text: '' });
    const [error, setError] = React.useState<{ error: boolean, message: string }>({ error: false, message: "" });
    const [companyErrors, setCompanyErrors] = React.useState<{ error: boolean, message: string[] }>({ error: false, message: [] })
    const [loading, setLoading] = React.useState<boolean>(false);
    const theme = useTheme();
    const SCHEMA = Yup.object({
        email: Yup.string().email(t('yup.email')).required(t('yup.required')),
        name: Yup.string().required(t('yup.required')),
        firstName: Yup.string().required(t('yup.required')),
    })
    const formik = useFormik({
        initialValues: {
            email: '',
            name: '',
            firstName: '',
        },
        validationSchema: SCHEMA,
        onSubmit: (values) => { },
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const getDelayed = React.useCallback(
        debounce((text, callback) => {
            setLoading(true)
            getCompanies(text);
        }, 200), []
    )

    const getCompanies = (name?: string): void => {
        companyService.getList(name, tenantId).then(res => {
            const newCompanies = [...companies, ...res]
            setCompanies(newCompanies.filter((company, index, self) => index === self.indexOf(company)));
        });
    }

    React.useEffect(() => {
        setCompanyErrors({ error: false, message: [] })
        setCompanies([]);
        formik.setValues({ email: '', name: '', firstName: '' })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const setCompanies = (companies: Company[]): void => {
        const newCompanies = companies.filter((company, index, self) => {
            const ids = self.map((company) => company.id)
            return index === ids.indexOf(company.id)
        });
        setLoading(false)
        _setCompanies(newCompanies);
    }

    const handleInputOrgChange = (event: any, newValue: string) => {
        setOrgInput(newValue);
        getDelayed(newValue, (res: any) => {
            console.log(res)
        });
    }

    const handleCompanyChange = (event: any, newValue: Company[]) => {
        setCompany(newValue);
    }

    const handleCreate = (): void => {
        formik.validateForm().then((res) => {
            formik.setFieldTouched('email', true, true);
            formik.setFieldTouched('name', true, true);
            formik.setFieldTouched('firstName', true, true);
            if (res.email === undefined && res.name === undefined && res.firstName === undefined && company.length > 0) {
                companyService.createResponsible(company.map(c => c.id), formik.values.email, formik.values.name, formik.values.firstName, tenantId).then((res) => {
                    formik.setValues({ email: '', name: '', firstName: '' })
                    setCompanyError({ error: false, text: '' });
                    refresh();
                    onClose();
                    formik.resetForm();
                })
                    .catch((err) => {
                        const {
                            response: {
                                data: { error },
                            },
                        } = err;
                        const { message } = error;
                        const { code } = error;
                        const { data: { companies } } = error
                        console.log(companies)
                        switch (code) {
                            case '406': {
                                setCompanyErrors({
                                    error: true, message: (companies as string[]).map((company: string) => {
                                        return t('settings.responsibles.errors.companyInUse', { company: company })
                                    })
                                })
                                break;
                            }
                            default: {
                                setError({ error: true, message: message });
                                break;
                            }
                        }
                    });
            } else if (company.length <= 0) {
                setCompanyError({ error: true, text: t('settings.responsibles.errors.company') });
            }
        });
    }

    const autoCompleteTextFieldProps: TextFieldProps = {
        label: t('settings.responsibles.labels.companyName'),
        size: 'small',
        error: companyError.error || companyErrors.error,
        helperText: companyError.text,
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth={'sm'}>
            <DialogTitle>
                {t('settings.responsibles.create.title')}
            </DialogTitle>
            <DialogContent>
                <DialogContentText>
                    {error.error && <Box color="error.main">{t(error.message)}</Box>}
                </DialogContentText>
                <Box sx={{ paddingTop: '0.50rem', paddingBottom: '1rem', height: 'fit-content' }}>
                    <TextField
                        label={t('requestIndividual.step2.responsibleEmail')}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.email}
                        error={formik.touched.email && Boolean(formik.errors.email)}
                        helperText={formik.touched.email && formik.errors.email}
                        size='small'
                        sx={{ width: '100%' }}
                        type='email'
                        id='email'
                        name='email'
                    />
                </Box>
                <Box sx={{ paddingBottom: '1rem', height: 'fit-content' }}>
                    <TextField
                        label={t('requestIndividual.step2.responsibleFirstName')}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.firstName}
                        error={formik.touched.firstName && Boolean(formik.errors.firstName)}
                        helperText={formik.touched.firstName && formik.errors.firstName}
                        size='small'
                        sx={{ width: '47.5%', marginRight: '5%' }}
                        type='text'
                        id='firstName'
                        name='firstName'
                    />
                    <TextField
                        label={t('requestIndividual.step2.responsibleLastName')}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.name}
                        error={formik.touched.name && Boolean(formik.errors.name)}
                        helperText={formik.touched.name && formik.errors.name}
                        size='small'
                        sx={{ width: '47.5%' }}
                        type='text'
                        id='name'
                        name='name'
                    />
                </Box>
                <Box>
                    <Autocomplete
                        multiple
                        options={companies}
                        onOpen={() => { setLoading(true); getCompanies() }}
                        loading={loading}
                        disableClearable
                        inputValue={orgInput}
                        onInputChange={handleInputOrgChange}
                        onChange={handleCompanyChange}
                        renderInput={(params) => <TextField
                            {...params}
                            {...autoCompleteTextFieldProps}
                            InputProps={{
                                ...params.InputProps,
                                endAdornment: (
                                    <React.Fragment>
                                        {loading ? <CircularProgress color="inherit" size={20} /> : null}
                                        {params.InputProps.endAdornment}
                                    </React.Fragment>
                                ),
                            }}
                        />
                        }
                        renderOption={(props, option, { selected }) => (
                            <li {...props} key={option.id}>
                                <Checkbox
                                    style={{ marginRight: 8 }}
                                    checked={company.map(c => c.id).includes(option.id)}
                                />
                                {option.name}
                            </li>
                        )}
                        filterOptions={(x) => x}
                        getOptionLabel={(option) => option.name}
                    />
                </Box>
                <Box sx={{
                    paddingTop: '0.5rem',
                    height: 'fit-content',
                    color: theme.palette.error.main,
                    // display: 'flex',
                    // flexDirection: 'column',
                    // justifyContent: 'center',
                    // alignItems: 'center'
                }}>
                    {companyErrors.message.map((message: string) => (
                        <Box sx={{paddingBottom: '0.5rem'}}>{message}</Box>
                    ))}
                </Box>
            </DialogContent>
            <DialogActions>
                <Button variant='contained' color='cancel' onClick={onClose}>{t('settings.responsibles.create.cancel')}</Button>
                <Button variant='contained' color='success' onClick={handleCreate}>{t('settings.responsibles.create.create')}</Button>
            </DialogActions>
        </Dialog>
    )
}

export default CreateResponsibleModal;
